import { jsPDF } from 'jspdf';
import { toEasternDateString, getEasternYear } from './dateHelper';

/**
 * Helper function to load image from URL and convert to base64
 * Works across all browsers with CORS handling
 * @param {string} url - The image URL
 * @param {number} timeout - Timeout in milliseconds (default 5000)
 * @returns {Promise<string>} - Base64 encoded image data
 */
const loadImageAsBase64 = (url, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    // Create a timeout to avoid hanging
    const timeoutId = setTimeout(() => {
      reject(new Error('Image load timeout'));
    }, timeout);

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      clearTimeout(timeoutId);
      try {
        const canvas = document.createElement('canvas');
        // Set reasonable max dimensions to keep PDF file size manageable
        const maxWidth = 300;
        const maxHeight = 200;
        let width = img.width;
        let height = img.height;
        
        // Scale down if needed while maintaining aspect ratio
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Draw with white background for transparency handling
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        const dataURL = canvas.toDataURL('image/png', 0.9);
        resolve(dataURL);
      } catch (e) {
        reject(e);
      }
    };
    
    img.onerror = (e) => {
      clearTimeout(timeoutId);
      reject(e);
    };
    
    // Add cache-busting to prevent CORS issues
    const cacheBuster = url.includes('?') ? '&_=' : '?_=';
    img.src = url + cacheBuster + Date.now();
  });
};

/**
 * Generate IRS Waiver Agreement PDF - Exact match to template
 * @param {Object} consentData - The signed consent document data
 * @param {Object} companyData - Company information
 * @param {Object} preparerData - Preparer information
 * @returns {jsPDF} - The generated PDF document
 */
export const generateIRSWaiverPDF = async (consentData, companyData = {}, preparerData = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = 20;

  // Company info
  const companyName = companyData?.name || 'Friends Tax Services LLC';
  const companyAddress1 = '568 GARDENIA DRIVE, BELL';
  const companyAddress2 = 'VERNON, PA 15012';
  const companyPhone = companyData?.phone || '(412)452-1754';
  const companyEmail = companyData?.email || 'friendstaxservicesllc@gmail.com';

  // Taxpayer info
  const taxpayer = consentData?.taxpayerInfo || {};
  const spouse = consentData?.spouseInfo || {};
  const dependents = consentData?.dependents || [];
  const careRecipient = consentData?.careRecipient || {};
  const taxYear = consentData?.taxYear || getEasternYear();
  const signDate = consentData?.signature?.date || toEasternDateString();

  // Colors
  const brownColor = [139, 90, 43]; // Brown/olive for headers
  const greenColor = [0, 100, 0]; // Dark green for company header
  const blueLink = [0, 0, 238]; // Blue for email link
  const goldHighlight = [255, 255, 150]; // Yellow highlight for section headers
  
  // Company logo (if available) - check multiple possible fields including landingPageSettings
  const companyLogoUrl = companyData?.landingPageSettings?.logoUrl || 
                      companyData?.logo || 
                      companyData?.logoUrl || 
                      companyData?.logoBase64 || 
                      companyData?.image || 
                      null;

  // Pre-load company logo if it's a URL
  let loadedLogoBase64 = null;
  if (companyLogoUrl) {
    if (companyLogoUrl.startsWith('data:')) {
      // Already base64
      loadedLogoBase64 = companyLogoUrl;
    } else if (companyLogoUrl.startsWith('http')) {
      // Need to load from URL
      try {
        loadedLogoBase64 = await loadImageAsBase64(companyLogoUrl);
      } catch (e) {
        console.log('Could not load company logo from URL:', e);
      }
    }
  }

  // Helper: Add company header with logo and info box
  const addCompanyHeader = () => {
    // Logo area (left side)
    if (loadedLogoBase64) {
      // Use actual company logo
      try {
        // Determine image format
        let format = 'PNG';
        if (loadedLogoBase64.includes('jpeg') || loadedLogoBase64.includes('jpg')) {
          format = 'JPEG';
        }
        doc.addImage(loadedLogoBase64, format, margin, 10, 45, 35);
      } catch (e) {
        console.log('Could not add company logo to PDF:', e);
        // Fallback to placeholder if image fails
        addLogoPlaceholder();
      }
    } else {
      addLogoPlaceholder();
    }
    
    function addLogoPlaceholder() {
      doc.setFillColor(139, 115, 85);
      doc.roundedRect(margin, 10, 45, 35, 3, 3, 'F');
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('F', margin + 22, 25, { align: 'center' });
      doc.setFontSize(6);
      doc.text(companyName.toUpperCase(), margin + 22, 35, { align: 'center' });
    }
    
    // Company info box (right side)
    doc.setDrawColor(0, 100, 0);
    doc.setLineWidth(0.5);
    doc.rect(pageWidth - margin - 75, 10, 75, 35);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...greenColor);
    doc.text(companyAddress1, pageWidth - margin - 72, 18);
    doc.text(companyAddress2, pageWidth - margin - 72, 23);
    doc.setFont('helvetica', 'normal');
    doc.text('Phone:', pageWidth - margin - 72, 28);
    doc.setFont('helvetica', 'bold');
    doc.text(companyPhone, pageWidth - margin - 55, 28);
    doc.setFont('helvetica', 'normal');
    doc.text('Email:', pageWidth - margin - 72, 33);
    doc.setTextColor(...blueLink);
    doc.text(companyEmail, pageWidth - margin - 55, 33);
    
    // Reset
    doc.setTextColor(0, 0, 0);
    
    // Green line under header
    doc.setDrawColor(0, 100, 0);
    doc.setLineWidth(1);
    doc.line(margin, 50, pageWidth - margin, 50);
    
    return 58;
  };

  // Helper: Add underlined field
  const addField = (label, value, x, y, lineWidth = 80) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(label, x, y);
    const labelWidth = doc.getTextWidth(label);
    
    // Underline
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(x + labelWidth + 2, y + 1, x + labelWidth + lineWidth, y + 1);
    
    // Value
    if (value) {
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), x + labelWidth + 4, y);
    }
  };

  // Helper: Add checkbox
  const addCheckbox = (label, checked, x, y, maxWidth = 160) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    // Checkbox
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(x, y - 3, 3.5, 3.5);
    
    if (checked) {
      doc.setFont('helvetica', 'bold');
      doc.text('X', x + 0.7, y);
      doc.setFont('helvetica', 'normal');
    }
    
    // Label text (wrapped)
    const lines = doc.splitTextToSize(label, maxWidth);
    lines.forEach((line, idx) => {
      doc.text(line, x + 6, y + (idx * 4));
    });
    
    return y + (lines.length * 4) + 2;
  };

  // Helper: Add highlighted section header
  const addHighlightedHeader = (text, x, y, width = 160) => {
    doc.setFillColor(...goldHighlight);
    doc.rect(x, y - 4, width, 6, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brownColor);
    doc.text(text, x + 2, y);
    doc.setTextColor(0, 0, 0);
    return y + 8;
  };

  // Helper function to add wrapped paragraph
  const addParagraph = (text, x, y, maxWidth, lineHeight) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach(line => {
      doc.text(line, x, y);
      y += lineHeight;
    });
    return y;
  };

  // ==================== PAGE 1: IRS Notice 2014-7 Form ====================
  yPos = addCompanyHeader();

  // Title
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  doc.text('IRS Notice 2014-7 Acknowledgment & Election Form', pageWidth / 2, yPos, { align: 'center' });
  yPos += 12;

  // Form fields
  doc.setTextColor(0, 0, 0);
  const fullName = taxpayer.name || `${taxpayer.firstName || ''} ${taxpayer.middleInitial || ''} ${taxpayer.lastName || ''}`.trim();
  const fullAddress = taxpayer.address || '';
  const cityStateZip = `${taxpayer.city || ''}, ${taxpayer.state || ''} ${taxpayer.zipCode || ''}`.trim();
  const ssn = taxpayer.ssn ? `***-**-${taxpayer.ssn.slice(-4)}` : '';

  addField('Tax Year:', taxYear.toString(), margin, yPos, 30);
  yPos += 8;
  addField('Name:', fullName, margin, yPos, 120);
  yPos += 8;
  addField('Address:', fullAddress, margin, yPos, 120);
  yPos += 8;
  addField('City, State, ZIP:', cityStateZip, margin, yPos, 100);
  yPos += 8;
  addField('Social Security Number (SSN):', ssn, margin, yPos, 50);
  yPos += 10;

  // Acknowledgment paragraph
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const ackText1 = `I, `;
  doc.text(ackText1, margin, yPos);
  const ack1Width = doc.getTextWidth(ackText1);
  
  // Name with underline
  doc.line(margin + ack1Width, yPos + 1, margin + ack1Width + 40, yPos + 1);
  if (fullName) {
    doc.text(fullName, margin + ack1Width + 2, yPos);
  }
  
  const ackText2 = `, acknowledge that I have received and reviewed information regarding IRS Notice`;
  doc.text(ackText2, margin + ack1Width + 42, yPos);
  yPos += 5;

  const ackPara = `2014-7, which provides guidance on the tax treatment of certain Medicaid waiver payments. I understand that: 1. Exclusion of Income: Medicaid waiver payments I received for providing non-medical care to an eligible individual in my home may be excluded from gross income if I meet the qualifications under IRS Notice 2014-7.`;
  const ackLines = doc.splitTextToSize(ackPara, pageWidth - (margin * 2));
  ackLines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 4;
  });
  yPos += 3;

  // Point 2
  const point2 = `2. Optional Inclusion for Tax Benefits: Even if my Medicaid waiver payments qualify for exclusion, I may elect to include them as earned income for purposes of certain tax credits, such as the Earned Income Tax Credit (EITC) or Additional Child Tax Credit (ACTC).`;
  const point2Lines = doc.splitTextToSize(point2, pageWidth - (margin * 2));
  point2Lines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 4;
  });
  yPos += 3;

  // Point 3
  const point3 = `3. Tax Filing Responsibility: It is my responsibility to report my income correctly and consult a tax professional if I need guidance on how IRS Notice 2014-7 applies to my situation.`;
  const point3Lines = doc.splitTextToSize(point3, pageWidth - (margin * 2));
  point3Lines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 4;
  });
  yPos += 3;

  // Point 4
  const point4 = `4. Amendment of Prior Returns: If I previously reported these payments as taxable income and later determine they qualify for exclusion, I may file an amended return (Form 1040-X) for potential tax adjustments.`;
  const point4Lines = doc.splitTextToSize(point4, pageWidth - (margin * 2));
  point4Lines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 4;
  });
  yPos += 6;

  // Election section
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  doc.text('Election (Choose One)', margin, yPos);
  yPos += 7;

  const election = consentData?.election || consentData?.consents?.medicaidElection || '';
  yPos = addCheckbox('I elect to EXCLUDE qualified Medicaid waiver payments from my gross income in accordance with IRS Notice 2014-7.', election === 'exclude', margin, yPos);
  yPos += 2;
  yPos = addCheckbox('I elect to INCLUDE my Medicaid waiver payments as earned income for tax credit eligibility.', election === 'include', margin, yPos);
  yPos += 6;

  // Certification & Signature
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  doc.text('Certification & Signature', margin, yPos);
  yPos += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  const certText = `I certify that the information provided is true and accurate to the best of my knowledge. I understand that this election may impact my tax return and agree to retain supporting documentation for tax purposes.`;
  const certLines = doc.splitTextToSize(certText, pageWidth - (margin * 2));
  certLines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 4;
  });
  yPos += 8;

  // Signature lines
  doc.setFontSize(10);
  addField('Taxpayer Signature:', '', margin, yPos, 70);
  doc.text('Date:', margin + 120, yPos);
  doc.line(margin + 132, yPos + 1, margin + 165, yPos + 1);
  if (signDate) doc.text(signDate, margin + 134, yPos);
  
  // Add taxpayer signature - handles both drawn (base64) and typed signatures
  if (consentData?.signature?.image) {
    try {
      if (consentData.signature.image.startsWith('data:')) {
        // Drawn signature - add as image
        doc.addImage(consentData.signature.image, 'PNG', margin + 40, yPos - 8, 35, 10);
      } else if (consentData.signature.image !== 'SIGNATURE_TOO_LARGE') {
        // Typed signature - render as cursive text
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(14);
        doc.text(consentData.signature.image, margin + 42, yPos - 2);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
      }
    } catch (e) { console.log('Could not add signature'); }
  }
  yPos += 12;

  // Preparer Section
  const preparerSection = consentData?.preparerSection || {};
  const preparerName = preparerSection.preparerName || preparerData?.name || '';
  const preparerSignatureDate = preparerSection.preparerSignatureDate || '';
  
  addField('Preparer Name (if applicable):', preparerName, margin, yPos, 60);
  yPos += 10;

  addField('Preparer Signature:', '', margin, yPos, 70);
  doc.text('Date:', margin + 120, yPos);
  doc.line(margin + 132, yPos + 1, margin + 165, yPos + 1);
  doc.text(preparerSignatureDate, margin + 134, yPos);
  
  // Add preparer signature - handles both drawn (base64) and typed signatures
  if (preparerSection.preparerSignatureImage) {
    try {
      if (preparerSection.preparerSignatureImage.startsWith('data:')) {
        // Drawn signature - add as image
        doc.addImage(preparerSection.preparerSignatureImage, 'PNG', margin + 40, yPos - 8, 35, 10);
      } else {
        // Typed signature - render as cursive text
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(14);
        doc.text(preparerSection.preparerSignatureImage, margin + 42, yPos - 2);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
      }
    } catch (e) { console.log('Could not add preparer signature'); }
  }

  // ==================== PAGE 2: To Whom It May Concern ====================
  doc.addPage();
  yPos = addCompanyHeader();
  yPos += 10;

  // Title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('TO WHOM IT MAY CONCERN', pageWidth / 2, yPos, { align: 'center' });
  yPos += 20;

  // Declaration paragraph
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  doc.text('Under penalties and perjury, I', margin, yPos);
  const perjuryWidth = doc.getTextWidth('Under penalties and perjury, I ');
  doc.line(margin + perjuryWidth, yPos + 1, margin + perjuryWidth + 50, yPos + 1);
  if (fullName) {
    doc.text(fullName, margin + perjuryWidth + 2, yPos);
  }
  doc.text('declare that I am', margin + perjuryWidth + 52, yPos);
  yPos += 8;

  const declarePara = `an individual care provider receiving payments under a State Medicaid Home and Community Based Service waiver program for care that I provide to`;
  const declareLines = doc.splitTextToSize(declarePara, pageWidth - (margin * 2));
  declareLines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 6;
  });
  yPos += 8;

  // Care recipient name line
  doc.line(margin, yPos, pageWidth - margin, yPos);
  if (careRecipient.name) {
    doc.setFont('helvetica', 'bold');
    doc.text(careRecipient.name, pageWidth / 2, yPos - 2, { align: 'center' });
    doc.setFont('helvetica', 'normal');
  }
  yPos += 10;

  // Address line
  doc.text('Address', pageWidth / 2, yPos, { align: 'center' });
  doc.line(margin + 20, yPos + 1, pageWidth - margin - 20, yPos + 1);
  if (careRecipient.address) {
    doc.text(careRecipient.address, pageWidth / 2, yPos + 8, { align: 'center' });
  }
  yPos += 15;

  const concernPara2 = `and is living in my home under the care recipient's plan of care. As per the IRS NOTICE 2014-7, I am excluding the income earned as waiver payment.`;
  const concern2Lines = doc.splitTextToSize(concernPara2, pageWidth - (margin * 2));
  concern2Lines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 6;
  });
  yPos += 30;

  // Signature
  addField('Signature:', '', margin, yPos, 80);
  doc.text('Date:', margin + 130, yPos);
  doc.line(margin + 143, yPos + 1, margin + 175, yPos + 1);
  if (signDate) doc.text(signDate, margin + 145, yPos);

  if (consentData?.signature?.image && consentData.signature.image.startsWith('data:')) {
    try {
      doc.addImage(consentData.signature.image, 'PNG', margin + 25, yPos - 8, 35, 10);
    } catch (e) { console.log('Could not add signature'); }
  }

  // ==================== PAGE 3: Client Information ====================
  doc.addPage();
  yPos = addCompanyHeader();

  // Date - Use Eastern Time
  const dateStr = toEasternDateString().replace(/\//g, ' / ');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  doc.text('Date:', pageWidth / 2 - 15, yPos);
  doc.text(dateStr, pageWidth / 2 + 5, yPos);
  yPos += 6;

  // CLIENT(S) INFORMATION header box
  doc.setFillColor(220, 220, 220);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'S');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('CLIENT(S) INFORMATION', pageWidth / 2, yPos + 5.5, { align: 'center' });
  yPos += 12;

  // PLEASE PRINT subtitle
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 6, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('PLEASE PRINT IF APPLICABLE', pageWidth / 2, yPos + 4, { align: 'center' });
  yPos += 12;

  // Taxpayer Info section
  yPos = addHighlightedHeader('Taxpayer Info', margin, yPos);
  
  const tpFirstName = taxpayer.firstName || fullName.split(' ')[0] || '';
  const tpLastName = taxpayer.lastName || fullName.split(' ').pop() || '';
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  addField('Name:', `${tpFirstName} ${tpLastName}`, margin + 5, yPos, 120);
  yPos += 8;
  addField('Email:', taxpayer.email || '', margin + 5, yPos, 120);
  yPos += 8;
  
  // DOB, SSN, Phone on same line
  doc.text('Date of Birth:', margin + 5, yPos);
  doc.line(margin + 30, yPos + 1, margin + 55, yPos + 1);
  if (taxpayer.dob) doc.text(taxpayer.dob, margin + 32, yPos);
  
  doc.text('SSN:', margin + 70, yPos);
  doc.line(margin + 80, yPos + 1, margin + 110, yPos + 1);
  if (taxpayer.ssn) doc.text(`***-**-${taxpayer.ssn.slice(-4)}`, margin + 82, yPos);
  
  doc.text('Phone:', margin + 125, yPos);
  doc.line(margin + 140, yPos + 1, margin + 175, yPos + 1);
  if (taxpayer.phone) doc.text(taxpayer.phone, margin + 142, yPos);
  yPos += 8;

  addField('Current Address:', `${fullAddress}${cityStateZip ? ', ' + cityStateZip : ''}`, margin + 5, yPos, 130);
  yPos += 12;

  // Spouse Info section
  yPos = addHighlightedHeader('Spouse info (if married and filing jointly or separately)', margin, yPos);
  
  const spFirstName = spouse.firstName || '';
  const spLastName = spouse.lastName || '';
  
  addField('Name:', `${spFirstName} ${spLastName}`.trim(), margin + 5, yPos, 120);
  yPos += 8;
  addField('Email:', spouse.email || '', margin + 5, yPos, 120);
  yPos += 8;
  
  doc.text('Date of Birth:', margin + 5, yPos);
  doc.line(margin + 30, yPos + 1, margin + 55, yPos + 1);
  if (spouse.dob) doc.text(spouse.dob, margin + 32, yPos);
  
  doc.text('SSN:', margin + 70, yPos);
  doc.line(margin + 80, yPos + 1, margin + 110, yPos + 1);
  if (spouse.ssn) doc.text(`***-**-${spouse.ssn.slice(-4)}`, margin + 82, yPos);
  
  doc.text('Phone:', margin + 125, yPos);
  doc.line(margin + 140, yPos + 1, margin + 175, yPos + 1);
  if (spouse.phone) doc.text(spouse.phone, margin + 142, yPos);
  yPos += 12;

  // Dependents Info section
  yPos = addHighlightedHeader('Dependents Info (if applicable)', margin, yPos);

  for (let i = 0; i < 3; i++) {
    const dep = dependents[i] || {};
    const depName = dep.firstName ? `${dep.firstName} ${dep.lastName || ''}`.trim() : '';
    
    doc.setFontSize(10);
    doc.text(`${i + 1}. Name:`, margin + 5, yPos);
    doc.line(margin + 25, yPos + 1, margin + 90, yPos + 1);
    if (depName) doc.text(depName, margin + 27, yPos);
    yPos += 8;

    doc.text('Date of Birth:', margin + 5, yPos);
    doc.line(margin + 30, yPos + 1, margin + 55, yPos + 1);
    if (dep.dob) doc.text(dep.dob, margin + 32, yPos);
    
    doc.text('SSN:', margin + 70, yPos);
    doc.line(margin + 80, yPos + 1, margin + 110, yPos + 1);
    if (dep.ssn) doc.text(`***-**-${dep.ssn.slice(-4)}`, margin + 82, yPos);
    
    doc.text('Relations to you:', margin + 120, yPos);
    doc.line(margin + 150, yPos + 1, margin + 175, yPos + 1);
    if (dep.relation) doc.text(dep.relation, margin + 152, yPos);
    yPos += 10;
  }

  // ==================== PAGE 4: Engagement Letter Part 1 ====================
  doc.addPage();
  yPos = addCompanyHeader();

  // Title
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  const engagementTitle = `ENGAGEMENT LETTER FOR ${taxYear - 1} TAX RETURN PREPARED IN ${taxYear}`;
  doc.text(engagementTitle, pageWidth / 2, yPos, { align: 'center' });
  
  // Underline the title
  const titleWidth = doc.getTextWidth(engagementTitle);
  doc.setDrawColor(...brownColor);
  doc.setLineWidth(0.5);
  doc.line((pageWidth - titleWidth) / 2, yPos + 2, (pageWidth + titleWidth) / 2, yPos + 2);
  yPos += 12;

  // To Our tax Clients
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  doc.text('To Our tax Clients:', margin, yPos);
  doc.line(margin, yPos + 1, margin + 40, yPos + 1);
  yPos += 10;

  // Engagement paragraphs
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  const para1 = `The purpose of this engagement letter is to outline your rights and responsibilities that you as a client must follow as well as ${companyName}'s participation and obligation to you, our valued client. On signing this letter, you acknowledge the following statements and agree to the following procedures with our firm to the best of your knowledge and understanding.`;
  yPos = addParagraph(para1, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 4;

  const para2 = `We will prepare your ${taxYear - 1} federal, state individual or local income tax returns using information you provide to us. We may ask for clarification of some items; it is your responsibility to provide information required for preparation of complete and accurate returns. You should keep all documents, canceled checks and other data that support your reported income and deductions. They may be necessary to prove accuracy and completeness of the returns to a taxing authority. You are responsible for the returns, so you should review them carefully before you sign them.`;
  yPos = addParagraph(para2, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 4;

  const para3 = `Our work will not include any procedures to discover faults or other irregularities. The only accounting or analysis work we will do is that which is necessary for preparation of your income tax returns. We must use our judgment in resolving questions where the tax law is unclear, or where there may be conflicts between the taxing authorities' interpretations of the law and other supportable positions. To avoid penalties, we will apply the "more likely than not" reliance standard to resolve such issues. You agree to honor our decisions regarding the need to make protective disclosures in your returns.`;
  yPos = addParagraph(para3, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 4;

  const para4 = `Penalties of as much as $100,000 can be imposed on you for failing to disclose participation in "reportable transactions," that is, certain arrangements the IRS has identified as potentially abusive. We will insist that all such transactions be properly disclosed. The law also imposes penalties when taxpayers understate their tax liability. If you have concerns about such penalties, please contact a tax attorney.`;
  yPos = addParagraph(para4, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 4;

  const para5 = `Taxpayers are required to maintain all the documents that form the basis of income, deductions, credits and payments shown on the return. In addition, some items have specific substantiation requirements set forth by the IRS (e.g., auto, meals & entertainment, and charitable contributions over $250, etc.). If you have questions as to the type of records required, please ask`;
  yPos = addParagraph(para5, margin, yPos, pageWidth - (margin * 2), 4);

  // ==================== PAGE 5: Engagement Letter Part 2 ====================
  doc.addPage();
  yPos = addCompanyHeader();

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  const para5cont = `us for advice in that regard. Your signature on this letter confirms that we have advised you of the record keeping requirements.`;
  yPos = addParagraph(para5cont, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 4;

  const para6 = `Your returns will be required to be filed electronically with the IRS. We will provide you with a copy of your final returns for review prior to electronic transmission. The IRS requires that you sign an e-file authorization form indicating that you have reviewed the return, it is correct to the best of your knowledge, and you authorize us to submit it electronically. We cannot transmit any return until we have the appropriate signed authorizations. E-filing of state returns varies by tax authority. If e-file is not available, paper copies will be provided for you to sign and mail.`;
  yPos = addParagraph(para6, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 4;

  const para7 = `Our fee for preparation of your tax returns will be based on the amount of time required at standard billing rates plus out-of-pocket expenses. All invoices are due and payable upon presentation. To the extent permitted by state law, an interest charge may be added to all accounts not paid within thirty (30) days. Any fee that is not paid within a 30-day period is subject to being turned over to our collection agency and then additional expenses will be added to your original invoice. If you request an additional copy of your tax returns, we will bill you at a rate of $20 per year per copy. The cost of E-mail a copy of the return to you will be $10 per year per copy. We cannot fax or e-mail to a third party due to disclosure rules set by the IRS.`;
  yPos = addParagraph(para7, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 4;

  const para8 = `We will retain copies of records you supplied to us along with our work papers for your engagement for a period of 7 years. After 7 years, our work papers and engagement files will be destroyed. All your original records will be returned to you at the end of this engagement. You should keep the original records in secure storage.`;
  yPos = addParagraph(para8, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 4;

  const para9 = `To affirm that this letter correctly summarizes your understanding of the arrangements for this work, please sign the enclosed copy of this letter in the space indicated and return it to us in the envelope provided. We appreciate your confidence in us. Please call if you have questions. Sincerely,`;
  yPos = addParagraph(para9, margin, yPos, pageWidth - (margin * 2), 4);
  yPos += 15;

  // Accepted by - Use preparer name from preparerSection
  doc.setFontSize(10);
  doc.text('Accepted by:', margin, yPos);
  // Add preparer name next to "Accepted by"
  const acceptedByPreparerName = preparerSection.preparerName || preparerData?.name || '';
  if (acceptedByPreparerName) {
    doc.setFont('helvetica', 'bold');
    doc.text(acceptedByPreparerName, margin + 28, yPos);
    doc.setFont('helvetica', 'normal');
  }
  doc.line(margin + 27, yPos + 1, margin + 100, yPos + 1);
  yPos += 25;

  // Your signature
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  doc.text('Your signature:', margin, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.line(margin + 30, yPos + 1, margin + 100, yPos + 1);
  
  doc.text('Date:', margin + 110, yPos);
  doc.line(margin + 123, yPos + 1, margin + 160, yPos + 1);
  if (signDate) doc.text(signDate, margin + 125, yPos);

  // Add signature image - handles both drawn (base64) and typed signatures
  if (consentData?.signature?.image) {
    try {
      if (consentData.signature.image.startsWith('data:')) {
        doc.addImage(consentData.signature.image, 'PNG', margin + 35, yPos - 10, 40, 12);
      } else if (consentData.signature.image !== 'SIGNATURE_TOO_LARGE') {
        // Typed signature - render as cursive text
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(14);
        doc.text(consentData.signature.image, margin + 35, yPos - 2);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
      }
    } catch (e) { console.log('Could not add signature'); }
  }
  yPos += 8;

  // Note under signature
  doc.setFontSize(9);
  doc.setTextColor(...brownColor);
  doc.text('Taxpayer (parent, if return is for a child under 18)', margin, yPos);
  yPos += 20;

  // Spouse signature
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  doc.text("Spouse's Signature:", margin, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.line(margin + 40, yPos + 1, margin + 100, yPos + 1);
  
  doc.text('Date:', margin + 110, yPos);
  doc.line(margin + 123, yPos + 1, margin + 160, yPos + 1);
  
  // Add spouse signature - handles both drawn (base64) and typed signatures
  if (consentData?.spouseSignature?.image) {
    try {
      if (consentData.spouseSignature.image.startsWith('data:')) {
        // Drawn signature - add as image
        doc.addImage(consentData.spouseSignature.image, 'PNG', margin + 45, yPos - 10, 40, 12);
      } else if (consentData.spouseSignature.image !== 'SIGNATURE_TOO_LARGE') {
        // Typed signature - render as cursive text
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(14);
        doc.text(consentData.spouseSignature.image, margin + 47, yPos - 2);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
      }
      if (consentData.spouseSignature.date) {
        doc.text(consentData.spouseSignature.date, margin + 125, yPos);
      }
    } catch (e) { console.log('Could not add spouse signature'); }
  }

  // ==================== PAGE 6: Photo ID Verification ====================
  doc.addPage();
  yPos = 25;

  // Title (no header on this page)
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Photo ID Verification', pageWidth / 2, yPos, { align: 'center' });
  yPos += 15;

  // Instructions
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brownColor);
  const instructions = 'Please upload valid government-issued photo identification for both the Consumer and the Caregiver. Both IDs must show the same current address for verification purposes.';
  const instLines = doc.splitTextToSize(instructions, pageWidth - (margin * 2));
  instLines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 5;
  });
  yPos += 10;

  // Consumer Photo ID Box
  doc.setFillColor(220, 220, 220);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 100, 'F');
  doc.setDrawColor(150, 150, 150);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 100, 'S');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...brownColor);
  doc.text('Consumer Photo ID Upload', margin + 5, yPos + 8);
  
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('(Accepted formats: JPG, PNG, PDF)', margin + 5, yPos + 15);

  // Add consumer photo if available
  if (consentData?.photoIDs?.consumer && consentData.photoIDs.consumer.startsWith('data:image')) {
    try {
      doc.addImage(consentData.photoIDs.consumer, 'JPEG', margin + 10, yPos + 20, 90, 75);
    } catch (e) { 
      console.log('Could not add consumer photo ID');
    }
  }
  
  yPos += 110;

  // Caregiver Photo ID Box
  doc.setFillColor(220, 220, 220);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 100, 'F');
  doc.setDrawColor(150, 150, 150);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 100, 'S');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...brownColor);
  doc.text('Caregiver Photo ID Upload', margin + 5, yPos + 8);
  
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('(Accepted formats: JPG, PNG, PDF)', margin + 5, yPos + 15);

  // Add caregiver photo if available
  if (consentData?.photoIDs?.caregiver && consentData.photoIDs.caregiver.startsWith('data:image')) {
    try {
      doc.addImage(consentData.photoIDs.caregiver, 'JPEG', margin + 10, yPos + 20, 90, 75);
    } catch (e) { 
      console.log('Could not add caregiver photo ID');
    }
  }
  
  yPos += 110;

  // Note at bottom
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(200, 50, 50); // Red color
  const noteText = 'Note: Ensure that the address on both IDs matches exactly. Incomplete or unclear documents may delay processing.';
  const noteLines = doc.splitTextToSize(noteText, pageWidth - (margin * 2));
  noteLines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 4;
  });

  // Add page numbers to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  return doc;
};

/**
 * Download the IRS Waiver Agreement PDF
 */
export const downloadIRSWaiverPDF = async (consentData, companyData = {}, preparerData = {}) => {
  const doc = await generateIRSWaiverPDF(consentData, companyData, preparerData);
  const taxpayerName = consentData?.taxpayerInfo?.name || 
    `${consentData?.taxpayerInfo?.firstName || ''} ${consentData?.taxpayerInfo?.lastName || ''}`.trim() || 
    'Taxpayer';
  const taxYear = consentData?.taxYear || getEasternYear();
  const fileName = `IRS_Waiver_${taxpayerName.replace(/\s+/g, '_')}_${taxYear}.pdf`;
  doc.save(fileName);
};

/**
 * Open the IRS Waiver Agreement PDF in a new window for printing
 */
export const printIRSWaiverPDF = async (consentData, companyData = {}, preparerData = {}) => {
  const doc = await generateIRSWaiverPDF(consentData, companyData, preparerData);
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfUrl);
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print();
    };
  }
};

const pdfGeneratorExports = {
  generateIRSWaiverPDF,
  downloadIRSWaiverPDF,
  printIRSWaiverPDF
};

export default pdfGeneratorExports;
